import React, { useCallback, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { DragItem } from '../types'
import { minutesToTime, timeToMinutes, PIXELS_PER_15_MIN, CALENDAR_START_MINUTE, CALENDAR_END_MINUTE } from '../utils/timeUtils'

interface DropTargetProps {
  time: string
  dayOfWeek: number
  children: React.ReactNode
  onMoveTodo: (todoId: string, newDayOfWeek: number, newStartTime: string) => void
}

const DropTarget: React.FC<DropTargetProps> = ({
  time,
  dayOfWeek,
  children,
  onMoveTodo
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'todo',
    drop: (item: DragItem, monitor) => {
      if (item.type === 'todo') {
        const client = monitor.getClientOffset()
        const gridEl = containerRef.current?.closest('.day-grid') as HTMLElement | null
        const rect = gridEl?.getBoundingClientRect()
        if (client && rect) {
          const relativeY = client.y - rect.top
          const offsetY = item.offsetY ?? 0
          const newTop = Math.max(0, relativeY - offsetY)
          // Map drop position to time relative to calendar start (07:00)
          let relativeMinutes = Math.round((newTop / PIXELS_PER_15_MIN) * 15)
          // Clamp within calendar range [07:00, 19:00)
          relativeMinutes = Math.max(0, Math.min(relativeMinutes, CALENDAR_END_MINUTE - CALENDAR_START_MINUTE - 15))
          const minutes = CALENDAR_START_MINUTE + relativeMinutes
          const newStartTime = minutesToTime(minutes)
          onMoveTodo(item.id, dayOfWeek, newStartTime)
        } else {
          // Fallback to target cell time
          onMoveTodo(item.id, dayOfWeek, time)
        }
      }
    },
    canDrop: (item: DragItem) => {
      return item.type === 'todo'
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  }), [dayOfWeek, time, onMoveTodo])

  const style: React.CSSProperties = {
    position: 'relative',
    backgroundColor: isOver && canDrop ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
  }

  return (
    <div
      ref={(node) => { drop(node); containerRef.current = node }}
      className="drop-target"
      style={style}
    >
      {children}
    </div>
  )
}

export default DropTarget